import { useTranslation } from 'react-i18next';
import { moduleNav } from '@platform/navigation';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { ModuleTile } from '@platform/ui';
import { recordCode } from '../header/recordCode';
import type { IRelatedCardProps } from './IRelatedCardProps';
import type { IRelatedItem } from './IRelatedItem';
import { relatedItemsOf } from './relatedItemsOf';
import { relatedKeyOf } from './relatedKeyOf';
import { useLinkedValues } from './useLinkedValues';
import { useReverseRefs } from './useReverseRefs';

const LIMIT = 5;

/**
 * Картка секції «Пов’язані записи» (прототип v4, relSecs): плитка модуля, підпис секції, кількість, «+ Зв’язати»; до 5 записів — зв'язані
 * й ті, що посилаються на запис полем, за фільтром секції (CRM «Акаунти» / «Контакти» — за роллю) і за номером (крапка статусу, номер
 * без префікса, назва; клік — міні-форма), далі «Ще N — у «Пов’язаних елементах»».
 */
export function RelatedCard({ ws, rt, record, related, filter, links, foreign, canEdit, onLink, onOpen }: IRelatedCardProps) {
  const { t } = useTranslation();
  const strings = useModuleStrings();
  const reverse = useReverseRefs(ws, rt, record, related.module);
  const linked: IRelatedItem[] = links.map((l) => ({
    id: l.otherRecordId, module: l.otherModule, recordType: l.otherRecordType, code: l.otherCode, display: l.otherDisplay, title: l.otherTitle, status: l.otherStatus,
  }));
  const { values, pending } = useLinkedValues(ws, reverse.typeKey, linked, Boolean(filter));
  const { items, total } = relatedItemsOf(linked, reverse.items, reverse.total, filter, values, pending);
  const moduleKey = reverse.module;
  const nav = moduleNav(moduleKey);
  // Підпис секції в рядках модуля: модуль, а для повтореного модуля — «модуль:Підпис» (CRM «crm:Accounts» · «crm:Contacts»).
  const labelKey = rt.related.filter((r) => r.module === related.module).length > 1 ? relatedKeyOf(related) : related.module;
  return (
    <div className="rec-relcard">
      <div className="rec-relcard__head">
        <ModuleTile icon={nav.icon} tone={nav.tone} size={22} />
        <strong className="rec-relcard__name">{strings.related(rt.key, labelKey, related.label)}</strong>
        <span className="rec-relcard__count">{total}</span>
        {canEdit && <button type="button" className="rec-relcard__link" title={t('recordPage.general.linkTitle')} aria-label={t('recordPage.general.linkTitle')} onClick={() => onLink(reverse.typeKey || moduleKey)}>{t('recordPage.general.link')}</button>}
      </div>
      {items.slice(0, LIMIT).map((i) => (
        <button key={i.id} type="button" className="rec-relcard__item" onClick={() => onOpen(i)}>
          <span className="rec-relcard__dot" style={{ background: foreign.status(i.module, i.recordType, i.status).color }} />
          <span className="rec-relcard__code" title={i.display}>{recordCode(i.display, i.code)}</span>
          <span className="rec-relcard__title">{i.title}</span>
        </button>
      ))}
      {!total && <div className="rec-relcard__empty">{t('recordPage.general.nothingLinked')}</div>}
      {total > LIMIT && <div className="rec-relcard__more">{t('recordPage.general.more', { count: total - LIMIT })}</div>}
    </div>
  );
}
